import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';

import { AppConstants } from '../../../../app.constants';
import { OrgSelectorService } from '../../../../core/services/org-selector.service';
import { AuthenticationsService } from '../../../../modules/authentications/authentications.service';
import { OrganizationDto } from '../../../../modules/organizations/dtos/organization';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import { Pagination } from '../../../../shared/components/pagination/models/pagination.model';
import { PaginationArgs } from '../../../../shared/components/pagination/models/pagination-args.model';
import { SortingType } from '../../../../shared/enums/sorting-type.enum';
import { MessageResponse } from '../../../../shared/models/message-response.model';
import { OrganizationsLegacyService } from '../../organizations-legacy.service';

@Component({
	selector: 'app-organizations',
	styleUrls: ['./organizations.component.scss'],
	templateUrl: './organizations.component.html',
})
export class OrganizationsComponent implements OnInit, OnDestroy {
	count: number = 0;
	currentPage: number = 1;
	isCreateEnabled: boolean = false;
	isDisableFavoriteAction: boolean = false;
	isLoading: boolean = true;
	limit: number = 10;
	organizations: OrganizationDto[] = [];
	searchForm: UntypedFormGroup;
	totalPages: number = 1;

	private readonly debounceTimeMs: number = AppConstants.debounce.time;
	private readonly sanusId: string = AppConstants.organization.id;
	private readonly searchOrganizationFilter = new Subject<string>();
	private searchSubscription$: Subscription = new Subscription();
	private toggleSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly organizationsService: OrganizationsService,
		private readonly legacyOrganizationsService: OrganizationsLegacyService,
		private readonly organizationSelector: OrgSelectorService,
		private readonly router: Router,
		private readonly authenticationsService: AuthenticationsService,
		private readonly snackBarService: SnackBarService,
	) {
		this.searchForm = this.fb.group({
			term: [null],
		});
	}

	calculateTotal(): void {
		let totalUsers = 0;
		let totalBeneficiaries = 0;

		this.organizations.forEach((organization) => {
			totalBeneficiaries += organization?._count?.beneficiaries || 0;
			totalUsers += organization?._count?.users || 0;

			if (organization.subsidiaries) {
				let totalBeneficiariesSubsidiary = 0;
				let totalUsersSubsidiary = 0;

				organization.subsidiaries.forEach((subsidiary) => {
					totalBeneficiariesSubsidiary +=
						subsidiary?._count?.beneficiaries || 0;
					totalUsersSubsidiary += subsidiary?._count?.users || 0;
				});

				totalBeneficiaries += totalBeneficiariesSubsidiary;
				totalUsers += totalUsersSubsidiary;
			}

			organization._count.totalBeneficiaries = totalBeneficiaries;
			organization._count.totalUsers = totalUsers;

			totalBeneficiaries = 0;
			totalUsers = 0;
		});
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.searchSubscription$.unsubscribe();
		this.toggleSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		this.searchOrganizations();

		const userOrganization = this.organizationSelector.getUserOrganization();

		if (userOrganization == this.sanusId) {
			this.isCreateEnabled = true;
		}

		this.searchOrganizationFilter
			.pipe(debounceTime(this.debounceTimeMs))
			.subscribe((searchValue) => {
				this.searchOrganizations(searchValue, true);
			});
	}

	onEditGroup(id: string, name: string): void {
		localStorage.setItem('org-id', id);

		this.organizationSelector.setOrganizationId(id);
		this.organizationSelector.setOrganizationName(name);

		this.router.navigate(['organizacoes', id]);
	}

	onPageChange(page: number): void {
		this.currentPage = page;

		this.searchOrganizations();
	}

	onSearch() {
		const term = this.searchForm.get('term')!.value;

		if (term?.length) {
			this.searchOrganizationFilter.next(term);
		} else {
			this.searchOrganizations(term);
		}
	}

	onSearchKeyUp(event: KeyboardEvent): void {
		const term = this.searchForm.get('term')!.value;

		if (event.key === 'Enter' && term?.length) {
			this.searchOrganizations(term);
		} else {
			if (!term?.length) {
				this.searchOrganizations();
			}
		}
	}

	onToggleFavorite(id: string): void {
		this.isDisableFavoriteAction = true;
		this.toggleSubscription$ = this.legacyOrganizationsService
			.toggleFavoriteOrganization(id)
			.pipe(
				finalize(() => {
					this.isDisableFavoriteAction = false;
				}),
			)
			.subscribe(
				(response: MessageResponse) => {
					if (response?.type === 'success') {
						this.snackBarService.showSnack(
							response.message || 'Favorito atualizado com sucesso.',
							'success',
						);
						this.searchOrganizations();
					} else {
						this.snackBarService.showSnack(
							response.message || 'Erro ao atualizar favorito.',
							'error',
						);
					}
				},
				(error: any) => {
					const errorMessage =
						error.error?.message || 'Erro ao tentar atualizar favorito.';
					this.snackBarService.showSnack(errorMessage, 'error');
				},
			);
	}

	searchOrganizations(filter?: string, isFilter: boolean = false): void {
		if (!isFilter) {
			this.isLoading = true;
			this.organizations = [];
		}

		const select = JSON.stringify({
			id: true,
			_count: {
				select: {
					beneficiaries: {
						where: { active: true },
					},
					users: {
						where: { active: true },
					},
				},
			},
			address: {
				select: {
					state: true,
				},
			},
			cnpj: true,
			is_matriz: true,
			isFavorite: true,
			name_economic_group: true,
			name_secondary: true,
			subsidiaries: {
				select: {
					id: true,
					_count: {
						select: {
							beneficiaries: {
								where: { active: true },
							},
							users: {
								where: { active: true },
							},
						},
					},
					address: {
						select: {
							state: true,
						},
					},
					cnpj: true,
					is_matriz: true,
					isFavorite: true,
					name_economic_group: true,
					name_secondary: true,
				},
			},
		});

		const session = this.authenticationsService.getSession();
		let where: string = JSON.stringify({
			active: true,
			is_matriz: session?.organization.isMatriz ? true : undefined,
		});

		if (filter) {
			where = JSON.stringify({
				OR: [
					{ name_secondary: { contains: filter, mode: 'insensitive' } },
					{ name: { contains: filter, mode: 'insensitive' } },
					{ cnpj: { contains: filter, mode: 'insensitive' } },
				],
				active: true,
			});
		}

		const orderBy = JSON.stringify({
			name_economic_group: SortingType.ASCENDING,
		});

		this.searchSubscription$ = this.organizationsService
			.findMany(
				new PaginationArgs({
					limit: this.limit,
					orderBy,
					page: this.currentPage,
					select,
					where,
				}),
			)
			.subscribe(
				async (response: Pagination<OrganizationDto>) => {
					this.organizations = response.result;
					if (this.organizations.length > 0) {
						this.calculateTotal();
					}
					this.count = response.count;
					this.totalPages = response.totalPages;
					this.isLoading = false;
				},
				() => {
					this.isLoading = false;
				},
			);
	}
}
